import { Badge, SideNavigationProps } from '@amzn/awsui-components-core';
import React from 'react';
import { isAlphaOrDevStage } from 'src/constants/generic-constants';
import ENVIRONMENT_VARIABLES from 'src/constants/environment-variables';

const createNavLink =
  (text: string, path: string) =>
  (businessGroupName: string): SideNavigationProps.Link => ({
    type: 'link' as const,
    text,
    href: `/${businessGroupName}/${path}`
  });

// Common finance owner tools
const COMMON_FINANCE_OWNER_TOOLS = [
  createNavLink('Access & Authorization', 'access-authorization'),
  createNavLink('Planning Cycle', 'planning-cycle'),
  createNavLink('Snapshots', 'snapshots')
];

// Non DMM specific finance owner tools
const NON_DMM_FINANCE_OWNER_TOOLS = [
  createNavLink('Budget Owner Transfer', 'budget-owner-transfer'),
  createNavLink('Expense Type Mapping', 'expense-type'),
  createNavLink('Useful Life Mapping', 'useful-life')
];

// Common planning links
const COMMON_PLANNING_LINKS = [createNavLink('Forecast Input', 'forecast-input'), createNavLink('PO Tagging', 'po-tagging')];

// DMM specific planning links
const DMM_SPECIFIC_PLANNING_LINKS = [createNavLink('Campaign', 'campaign')];

// Reports
const REPORTS_LINKS = [
  createNavLink('Forecast', 'forecast-report'),
  createNavLink('Variance', 'variance-report'),
  createNavLink('Actuals', 'actuals-report'),
  createNavLink('Actuals vs Forecast', 'actuals-vs-forecast-report')
];

// Budget Tracker is enabled in alpha, beta — disabled in prod
export const isBudgetTrackerEnabled = ENVIRONMENT_VARIABLES.env.Stage !== 'prod';

const BUDGET_TRACKER_LINK = createNavLink('Budget Tracker', 'budget-tracker');

// Report Builder (AG Studio) — developer-only, flagged as New.
const REPORT_BUILDER_LINK = (businessGroupName: string): SideNavigationProps.Link => ({
  type: 'link' as const,
  text: 'Report Builder',
  href: `/${businessGroupName}/report-builder`,
  info: React.createElement(Badge, { color: 'blue' }, 'New')
});

// DMM-specific reports (alpha/dev only)
const DMM_REPORTS_LINKS = isAlphaOrDevStage(ENVIRONMENT_VARIABLES.env.Stage)
  ? [createNavLink('DMM Marketing Spend', 'dmm-marketing-spend')]
  : [];

export const NAVIGATION_CONFIG = {
  financeOwnerTools: {
    type: 'section' as const,
    text: 'Finance Owner Tools',
    items: (businessGroupName: string, isDmmBusinessGroup: boolean): SideNavigationProps.Link[] => [
      ...COMMON_FINANCE_OWNER_TOOLS.map((link) => link(businessGroupName)),
      ...(isDmmBusinessGroup ? [] : NON_DMM_FINANCE_OWNER_TOOLS.map((link) => link(businessGroupName)))
    ]
  },
  planning: {
    type: 'section' as const,
    text: 'Planning',
    items: (businessGroupName: string, isDmmBusinessGroup: boolean = false): SideNavigationProps.Link[] => [
      ...COMMON_PLANNING_LINKS.map((link) => link(businessGroupName)),
      ...(isDmmBusinessGroup ? DMM_SPECIFIC_PLANNING_LINKS.map((link) => link(businessGroupName)) : [])
    ]
  },
  reports: {
    type: 'section' as const,
    text: 'Reports',
    items: (businessGroupName: string, isDmmBusinessGroup: boolean = false, isDev: boolean = false): SideNavigationProps.Link[] => [
      ...REPORTS_LINKS.map((link) => link(businessGroupName)),
      ...(isBudgetTrackerEnabled ? [BUDGET_TRACKER_LINK(businessGroupName)] : []),
      ...(isDmmBusinessGroup ? DMM_REPORTS_LINKS.map((link) => link(businessGroupName)) : []),
      ...(isDev ? [REPORT_BUILDER_LINK(businessGroupName)] : [])
    ]
  }
};
